import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LiveKitRoom,
  VideoConference,
  PreJoin,
  type LocalUserChoices
} from '@livekit/components-react'
import { Track } from 'livekit-client'
import { START_LIVESTREAM, END_LIVESTREAM } from '../graphql/operations'

interface StartLivestreamData {
  startLivestream: {
    livestream: { id: string; title: string; status: string } | null
    publisherToken: string | null
    wsUrl: string | null
    userErrors: { field?: string[]; message: string }[]
  }
}

interface EndLivestreamData {
  endLivestream: {
    livestream: { id: string; status: string; endedAt: string | null } | null
    userErrors: { field?: string[]; message: string }[]
  }
}

type Phase = 'pre-join' | 'connecting' | 'live' | 'ended'

export default function StudioRoom() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('pre-join')
  const [token, setToken] = useState<string | null>(null)
  const [wsUrl, setWsUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [userChoices, setUserChoices] = useState<LocalUserChoices | null>(null)

  const [startLivestream, { loading: starting }] = useMutation<StartLivestreamData>(START_LIVESTREAM)
  const [endLivestream] = useMutation<EndLivestreamData>(END_LIVESTREAM)

  // Fire EndLivestream automatically on unmount/disconnect so streams
  // don't accumulate in "live" status when the user just closes the tab.
  useEffect(() => {
    return () => {
      if (id && phase === 'live') {
        // Best-effort — we don't await this on unmount.
        endLivestream({ variables: { id } }).catch(err => {
          console.warn('EndLivestream on unmount failed', err)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, phase])

  async function handleJoin(choices: LocalUserChoices) {
    if (!id) return
    setUserChoices(choices)
    setPhase('connecting')
    setError(null)

    try {
      const { data } = await startLivestream({ variables: { id } })
      const payload = data?.startLivestream
      const userErr = payload?.userErrors?.[0]?.message
      if (userErr) {
        setError(userErr)
        setPhase('pre-join')
        return
      }
      if (!payload?.publisherToken || !payload.wsUrl) {
        setError('Server didn\'t return publisher credentials.')
        setPhase('pre-join')
        return
      }
      setToken(payload.publisherToken)
      setWsUrl(payload.wsUrl)
      setPhase('live')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setPhase('pre-join')
    }
  }

  async function handleEnd() {
    if (!id) {
      navigate('/', { replace: true })
      return
    }
    try {
      await endLivestream({ variables: { id } })
    } catch (err) {
      console.warn('EndLivestream failed', err)
    }
    setPhase('ended')
    setToken(null)
    setWsUrl(null)
  }

  if (phase === 'ended') {
    return (
      <div className="studio-end">
        <h1>Stream ended</h1>
        <p className="muted">Your livestream has been marked ended on the server.</p>
        <button className="btn-primary" onClick={() => navigate('/', { replace: true })}>
          Back to streams
        </button>
      </div>
    )
  }

  if (phase === 'pre-join' || phase === 'connecting') {
    return (
      <div className="studio-prejoin">
        <h1>Prepare to go live</h1>
        <p className="muted">Pick your camera and mic, then start the stream.</p>
        {error && <p className="error">{error}</p>}
        <div className="prejoin-wrapper">
          <PreJoin
            defaults={{ username: 'Streamer', videoEnabled: true, audioEnabled: true }}
            onSubmit={handleJoin}
            joinLabel={starting ? 'Starting…' : 'Go live'}
            persistUserChoices
          />
        </div>
      </div>
    )
  }

  // phase === 'live' — connected to the LiveKit room as publisher.
  return (
    <div className="studio-live">
      <div className="studio-live-header">
        <span className="badge badge-live">LIVE</span>
        <button className="btn-danger" onClick={handleEnd}>End stream</button>
      </div>
      {token && wsUrl && (
        <LiveKitRoom
          token={token}
          serverUrl={wsUrl}
          connect={true}
          // Publisher-side defaults — we always want camera + mic on
          // when the room loads. The PreJoin choices already prompted
          // for permissions; this just means LiveKit publishes them.
          video={userChoices?.videoEnabled ?? true}
          audio={userChoices?.audioEnabled ?? true}
          // Disconnecting the room (browser navigates away, network
          // dies, etc.) propagates to handleEnd via the cleanup hook.
          onDisconnected={() => {
            if (phase === 'live') void handleEnd()
          }}
          data-lk-theme="default"
        >
          <VideoConference
            // Hide the chat panel for now — Tempest's chat travels via
            // a separate data-channel topic and isn't wired in the
            // studio yet.
            chatMessageFormatter={undefined}
          />
        </LiveKitRoom>
      )}
      <p className="muted small">
        Tip: ask a viewer on the iOS app to join this stream — they should see and hear you within ~1s. {' '}
        <span style={{ display: 'inline-block', padding: '2px 6px', background: '#f3f3f3', borderRadius: 4 }}>
          Track topic: {Track.Source.Camera}
        </span>
      </p>
    </div>
  )
}
